import { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, useApp, Typography, Alert, Space, Tag, Tooltip, theme } from '../ui';
import { Hash, ArrowLeft, MessageCircle, Zap, Copy } from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useLoginModal } from '../store/loginModal';
import { request } from '../api/client';
import { brand } from '../assets/brand';
import { PhoneInput } from './PhoneInput';
import { displayMobile } from '../lib/validators';

/**
 * WhatsApp-OTP sign-in rendered as a MODAL (replaces the old /login page). It is
 * mounted once at the app root and driven by the global `useLoginModal` store, so
 * the public-header "Login" button, the legacy /login URL, and the auth guards
 * all open the same dialog over whatever page the visitor is on.
 *
 * The two helper components below redirect the old /login route and unauthenticated
 * guard hits back to the home page while popping this modal.
 */
export function LoginModal() {
  const open = useLoginModal((s) => s.open);
  const redirectTo = useLoginModal((s) => s.redirectTo);
  const closeLogin = useLoginModal((s) => s.closeLogin);

  const { message } = useApp();
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const { token } = theme.useToken();
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';
  const accent = isDark ? brand.primaryLight : brand.primaryDark;

  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [mobile, setMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  // When the code on screen stops being valid, and a counter that increments on every
  // successful send. The counter is what re-arms the dev auto-submit on a RESEND: the dev
  // code is always the same string, so `devCode` alone never changes and the effect below
  // would not re-run.
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [sendSeq, setSendSeq] = useState(0);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [form] = Form.useForm<{ code: string }>();

  const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - nowTs) / 1000)) : null;
  const codeExpired = secondsLeft === 0;

  // Reset to a clean state every time the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setStep('mobile'); setMobile(''); setSending(false); setDevCode(null);
      setExpiresAt(null); setSendSeq(0); form.resetFields();
    }
  }, [open, form]);

  // Tick only while a code is outstanding — drives the countdown and flips the box to
  // "expired" the moment it lapses, so the user is told BEFORE they submit a dead code
  // rather than after the server rejects it.
  useEffect(() => {
    if (step !== 'otp' || !expiresAt) return;
    setNowTs(Date.now());
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [step, expiresAt]);

  // DEV convenience: the server echoes the OTP back as `devCode` (non-production only). When present,
  // auto-fill the box AND auto-submit so a developer signs in with a single "Send OTP" click — no typing.
  // Two independent guards keep this out of production: the server does not send `devCode` there, and
  // the setter below is compiled out of a production bundle entirely.
  useEffect(() => {
    if (step !== 'otp' || !devCode) return;
    form.setFieldsValue({ code: devCode });
    const t = setTimeout(() => form.submit(), 400);
    return () => clearTimeout(t);
  }, [step, devCode, sendSeq, form]);

  const onRequestOtp = async () => {
    // `mobile` is already E.164 (from the country-aware PhoneInput); the server canonicalises it too.
    if (!mobile || !isValidPhoneNumber(mobile)) {
      message.warning('Enter a valid phone number.');
      return;
    }
    setSending(true);
    try {
      const r = await request<any>({ url: '/auth/login/otp/request', method: 'POST', data: { mobile } });
      if (r.delivery === 'failed') {
        message.warning('OTP generated but WhatsApp delivery failed — check Meta config.');
      } else {
        message.success(r.message ?? 'OTP sent via WhatsApp');
      }
      // Never act on an echoed OTP in a production bundle. The server only returns `devCode` outside
      // production, but this branch auto-fills AND auto-submits, so a single misconfigured NODE_ENV
      // would turn the login box into a code display that signs itself in. `import.meta.env.PROD` is
      // statically replaced at build time, so the whole path is dropped from the production bundle.
      setDevCode(!import.meta.env.PROD && typeof r.devCode === 'string' ? r.devCode : null);
      // Drive the countdown off the server's own expiry rather than a local guess, so the
      // box never claims a code is still good after the server has stopped accepting it.
      const exp = r.expiresAt ? new Date(r.expiresAt).getTime() : null;
      setExpiresAt(Number.isFinite(exp as number) ? (exp as number) : null);
      setNowTs(Date.now());
      form.setFieldsValue({ code: '' }); // never leave a superseded code sitting in the box
      setSendSeq((n) => n + 1);
      setStep('otp');
    } catch (e: any) {
      const err = e.response?.data?.error;
      // OTP issuance is capped per IP per minute; the throttler surfaces as a bare
      // "ThrottlerException: Too Many Requests", which tells the user nothing they can act on.
      const throttled = e.response?.status === 429 || /too many requests/i.test(err?.message ?? '');
      message.error(
        throttled
          ? 'Too many OTP requests — please wait a minute before asking for another code.'
          : (err?.message ?? 'Failed to send OTP'),
      );
    } finally {
      setSending(false);
    }
  };

  const onVerifyOtp = async (values: { code: string }) => {
    try {
      // clientType marks this as the WEB channel, so the server does not apply the mobile device
      // binding. RETAILER, FARMER, SALES_PERSON and SALES_HEAD are bound on their handsets but are
      // legitimate web users, and a browser has no device to bind — without this field the server
      // cannot tell the channels apart, and would have to either lock web out or leave the binding
      // unenforced. A web login never creates, replaces or clears the mobile binding.
      const r = await request<any>({
        url: '/auth/login/otp/verify',
        method: 'POST',
        data: { mobile, code: values.code, clientType: 'WEB' },
      });
      setTokens(r.accessToken, r.refreshToken, {
        id: r.user.id,
        fullName: r.user.fullName,
        role: r.user.role,
        roles: r.user.roles ?? [r.user.role],
        permissions: r.user.permissions,
        territoryId: r.user.territoryId,
      });
      message.success(`Welcome, ${r.user.fullName}`);
      closeLogin();
      // Back to where they were headed, else the per-role landing (RoleHome) —
      // never a hardcoded /dashboard (403s for Retailer/Farmer/Accounts).
      navigate(redirectTo || '/home');
    } catch (e: any) {
      const err = e.response?.data?.error;
      // Two different failures, two different ways out:
      //
      //   • the CHALLENGE is dead (expired / attempts exhausted / already consumed) — no code
      //     can ever satisfy it, so retire it and hand the user the Resend affordance. This is
      //     what breaks the "press Verify → 'OTP expired' → press Verify again" loop, which was
      //     unescapable while the box kept re-posting the same dead code.
      //   • the CODE was simply wrong — the challenge is still good, so just clear the field
      //     and let them retype.
      const challengeDead = err?.code === 'OTP_EXPIRED' || err?.code === 'OTP_BLOCKED'
        || /no active otp/i.test(err?.message ?? '');
      form.setFieldsValue({ code: '' });
      if (challengeDead) {
        setExpiresAt(Date.now()); // → secondsLeft 0 → the Resend affordance takes over
        setDevCode(null);
      }
      message.error(err?.message ?? 'Invalid OTP');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) closeLogin(); }}
      width={420}
      title={
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
          <img src="/logo.png" alt="Green Bay Biotech International" style={{ height: 60, objectFit: 'contain' }} />
        </div>
      }
    >
      <div style={{ paddingTop: 8 }}>
        <Typography.Title level={4} style={{ color: accent, marginBottom: 4 }}>
          {step === 'mobile' ? 'Sign in' : 'Verify OTP'}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
          {step === 'mobile'
            ? 'We will send a one-time code to your WhatsApp.'
            : `Code sent via WhatsApp to ${displayMobile(mobile)}.`}
        </Typography.Paragraph>

        {step === 'mobile' && (
          <Form layout="vertical">
            <Form.Item label="Mobile number" required>
              <PhoneInput value={mobile} onChange={(v) => setMobile(v ?? '')} placeholder="Mobile number" />
            </Form.Item>
            <Button
              variant="primary"
              size="lg"
              block
              onClick={onRequestOtp}
              loading={sending}
              icon={<MessageCircle size={16} />}
              style={{ background: brand.primary, height: 48, fontWeight: 600 }}
            >
              Send OTP via WhatsApp
            </Button>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16, ...(isDark ? {} : { background: '#F0F7F0', border: '1px solid #E0EEDF' }) }}
              message={<span style={{ fontSize: 12 }}>New farmer? Your mobile becomes your account on first sign-in.</span>}
            />
          </Form>
        )}

        {step === 'otp' && (
          <Form layout="vertical" form={form} onFinish={onVerifyOtp}>
            {devCode && (
              <Alert
                type="warning"
                showIcon
                icon={<Zap size={16} />}
                style={{ marginBottom: 12, ...(isDark ? {} : { background: '#FFFBE6', border: '1px solid #FFE58F' }) }}
                message={
                  <Space>
                    <span style={{ fontSize: 12 }}>Dev mode — OTP:</span>
                    <Tag tone="gold" style={{ fontSize: 16, fontWeight: 600, letterSpacing: 2, padding: '2px 10px', margin: 0 }}>
                      {devCode}
                    </Tag>
                    <Tooltip title="Auto-fill the box below">
                      <Button size="sm" variant="text" icon={<Copy size={16} />} onClick={() => form.setFieldsValue({ code: devCode })}>
                        Use it
                      </Button>
                    </Tooltip>
                  </Space>
                }
                description={
                  <span style={{ fontSize: 11, color: isDark ? token.colorTextSecondary : '#8c6e00' }}>
                    Server is running in NODE_ENV=development. This banner is hidden in production builds.
                  </span>
                }
              />
            )}
            <Form.Item name="code" label="6-digit OTP" rules={[{ required: true, len: 6 }]}>
              <Input
                prefix={<Hash size={16} style={{ color: brand.primary }} />}
                placeholder="● ● ● ● ● ●"
                size="large"
                maxLength={6}
                autoFocus
                style={{ letterSpacing: 8, fontWeight: 600, fontSize: 18 }}
              />
            </Form.Item>
            {/* How long the code on screen is still good for, and the way to get a new one.
                Without this the OTP step was a dead end: once the code lapsed, "Verify" could
                only ever fail, and the sole escape was "Use a different number" — which reads
                as "change your phone number", not "send me another code". */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, minHeight: 24 }}>
              <span style={{ fontSize: 12, color: codeExpired ? token.colorError : token.colorTextTertiary }}>
                {secondsLeft == null
                  ? null
                  : codeExpired
                    ? 'This code has expired.'
                    : `Code valid for ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
              </span>
              <Button
                variant="text"
                size="sm"
                loading={sending}
                disabled={sending || (!codeExpired && (secondsLeft ?? 0) > 240)}
                onClick={onRequestOtp}
                icon={<MessageCircle size={14} />}
              >
                {codeExpired ? 'Send a new code' : 'Resend'}
              </Button>
            </div>
            <Button variant="primary" size="lg" type="submit" block disabled={codeExpired}
              style={{ background: brand.primary, height: 48, fontWeight: 600 }}>
              Verify &amp; Sign in
            </Button>
            <Button variant="text" icon={<ArrowLeft size={16} />} block style={{ marginTop: 8 }}
              onClick={() => { setStep('mobile'); setDevCode(null); setExpiresAt(null); form.resetFields(); }}>
              Use a different number
            </Button>
          </Form>
        )}

        <div style={{ marginTop: 24, fontSize: 11, color: token.colorTextTertiary, textAlign: 'center' }}>
          By signing in you agree to our{' '}
          <a href="/terms" target="_blank" rel="noreferrer" style={{ color: token.colorTextSecondary, textDecoration: 'underline' }}>
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: token.colorTextSecondary, textDecoration: 'underline' }}>
            Privacy Policy
          </a>{' '}
          · {brand.tagline}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Stand-in for the retired /login route: opens the login modal and bounces to the
 * public home page (so old bookmarks / links still land somewhere sensible). Honours
 * a `?redirect=` query param for post-login navigation.
 */
export function LoginRedirect() {
  const openLogin = useLoginModal((s) => s.openLogin);
  const [params] = useSearchParams();
  useEffect(() => { openLogin(params.get('redirect')); }, [openLogin, params]);
  return <Navigate to="/" replace />;
}

/**
 * Used by the auth guards when an unauthenticated user hits a protected page:
 * remember where they were headed, pop the login modal, and send them to home.
 */
export function RedirectToLogin({ from }: { from?: string }) {
  const openLogin = useLoginModal((s) => s.openLogin);
  useEffect(() => { openLogin(from ?? null); }, [openLogin, from]);
  return <Navigate to="/" replace />;
}
